import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QrScanner from 'qr-scanner';
import { parseQRCodeUrl } from '../../utils/qrCode';

const QRCodeScanner = () => {
  const videoRef = useRef(null);
  const scannerRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('pending');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if camera is available
    QrScanner.hasCamera().then(setHasCamera);

    return () => {
      if (scannerRef.current) {
        scannerRef.current.destroy();
      }
    };
  }, []);

  const handleScanResult = (result) => {
    console.log('QR Code scanned:', result);
    
    try {
      // Try to extract itemId from QR code
      let itemId = result;
      
      // If it looks like a URL, try to parse it
      if (result.includes('://') || result.startsWith('/')) {
        try {
          const parsedId = parseQRCodeUrl(result);
          if (parsedId) {
            itemId = parsedId;
          }
        } catch (err) {
          console.log('Not a URL format, using raw text');
        }
      } else if (result.includes('/item/')) {
        // Handle URL patterns that might not have protocol
        try {
          const url = new URL(result, window.location.origin);
          const pathname = url.pathname;
          const itemIdMatch = pathname.match(/\/item\/([a-zA-Z0-9-]+)/);
          
          if (itemIdMatch) {
            itemId = itemIdMatch[1];
          }
        } catch (urlError) {
          console.log('Error parsing URL pattern:', urlError);
        }
      }
      
      setSuccess(`QR Code found! Opening item: ${itemId}`);
      console.log(`Navigating to item: ${itemId}`);
      
      // Stop scanning before navigation
      stopScanning();
      
      // Navigate to item page
      navigate(`/item/${itemId}`);
      
    } catch (err) {
      console.error('Error processing QR code:', err);
      setError('Error processing QR code: ' + err.message);
    }
  };

  const startScanning = async () => {
    if (!videoRef.current || !hasCamera) return;
    
    try {
      setError('');
      setSuccess('');
      
      scannerRef.current = new QrScanner(
        videoRef.current,
        (result) => handleScanResult(result.data),
        {
          returnDetailedScanResult: true,
          highlightScanRegion: true,
          highlightCodeOutline: true,
          preferredCamera: 'environment', // Use back camera on mobile
        }
      );
      
      await scannerRef.current.start();
      setIsScanning(true);
      setCameraPermission('granted');
    } catch (error) {
      console.error('Error starting scanner:', error);
      setCameraPermission('denied');
      const errorMessage = error.message || 'Failed to start camera';
      setError('Camera Error: ' + errorMessage);
          }
  };
      
  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.stop();
      setIsScanning(false);
    }
  };
  
  const handleRetry = () => {
    setError('');
    setSuccess('');
    startScanning();
  };

  if (!hasCamera) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md text-center">
          <div className="text-6xl mb-4">📷</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No Camera Available</h2>
          <p className="text-gray-600 mb-4">No camera was detected on this device</p>
          <button
            onClick={() => navigate('/')}
            className="bg-gray-700 hover:bg-gray-800 text-white px-6 py-2 rounded-md transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const getVideoBackgroundColor = () => {
    if (cameraPermission === 'granted' && isScanning) {
      return 'bg-black';
    }
    return 'bg-gray-500'; // Neutral grey until permission granted
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">QR Code Scanner</h1>
          <p className="text-gray-600">Scan equipment QR codes to view details</p>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        {success && (
          <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md">
            {success}
          </div>
        )}

        {/* Camera View */}
        <div className="space-y-4">
          <div className={`relative ${getVideoBackgroundColor()} rounded-lg overflow-hidden`}>
            <video
              ref={videoRef}
              className="w-full mx-auto rounded-lg block"
              style={{ 
                display: 'block',
                minHeight: isScanning ? '300px' : '200px',
                objectFit: 'cover'
              }}
              playsInline
              muted
            />
            
            {/* Camera overlay with scanning guide */}
            {isScanning && (
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div className="relative">
                  {/* Scanning frame */}
                  <div className="w-64 h-64 border-4 border-white rounded-lg relative">
                    {/* Corner indicators */}
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-yellow-500 rounded-tl-lg"></div>
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-yellow-500 rounded-tr-lg"></div>
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-yellow-500 rounded-bl-lg"></div>
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-yellow-500 rounded-br-lg"></div>
                    
                    {/* Center crosshair */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-8 h-0.5 bg-yellow-500"></div>
                      <div className="absolute w-0.5 h-8 bg-yellow-500"></div>
                    </div>
                  </div>
                  
                  {/* Instruction text */}
                  <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap">
                    Position QR code within the frame
                  </div>
                </div>
              </div>
            )}
            
            {!isScanning && (
              <div className={`absolute inset-0 flex items-center justify-center ${cameraPermission === 'granted' ? 'bg-gray-900 bg-opacity-50' : 'bg-gray-500 bg-opacity-75'}`}>
                <div className="text-center text-white">
                  <p className="opacity-75">
                    {cameraPermission === 'pending' ? 'Ready to scan QR code' :
                     cameraPermission === 'denied' ? 'Camera permission needed' :
                     'Ready to scan QR code'}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Control Buttons */}
          <div className="flex flex-col space-y-3">
            {!isScanning ? (
              <button
                onClick={startScanning}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-md font-medium transition-colors flex items-center justify-center"
              >
                Start Camera
              </button>
            ) : (
              <button
                onClick={stopScanning}
                className="w-full bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-md font-medium transition-colors flex items-center justify-center"
              >
                <span className="mr-2">⏹️</span>
                Stop Camera
              </button>
            )}
      
      {error && (
          <button 
            onClick={handleRetry}
                className="w-full bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium transition-colors"
              >
                Retry
              </button>
            )}
            
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gray-700 hover:bg-gray-800 text-white px-6 py-3 rounded-md font-medium transition-colors"
          >
              Back to Dashboard
          </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeScanner;
